import { INGEST_FIELD_LABELS, type BatchOptionFor, type BatchRefKey, type BatchRowKind, type BatchRowOp } from '@contracts/ingest';
import {
  CONTRACT_STATUS_LABEL, ENGAGEMENT_LABEL, LEVEL_LABEL, PARTNER_LABEL_LABEL, STAKEHOLDER_TYPE_LABEL,
} from '@domain/stakeholders';
import { formatDate } from '../../../components/DateField';
import { PARTNER_KIND_LABEL, PRIORITY_LABEL } from '../../../lib/labels';
import { formatMoney, monthLabel } from '../../finance/money';

/**
 * What the card lets a person edit on a row (S5 §8.3): each kind's fields for its op, the server's extract subsets
 * (`ingest/schema.ts` `subsetOf`) word for word — a unit test holds the two together — and how each one is edited
 * and read back.
 */

const ANALYSIS = ['power', 'interest', 'engagementCurrent', 'engagementDesired', 'strategyNote'] as const;
const CONTRACT = ['title', 'contractNo', 'value', 'startDate', 'endDate', 'status', 'note'] as const;
const PARTNER = ['name', 'labels', 'taxCode', 'phone', 'email', 'address', 'website', 'note'] as const;
const CONTACT = ['name', 'title', 'phone', 'email', 'note'] as const;

export const FIELD_SUBSETS: Readonly<Record<BatchRowKind, Partial<Record<BatchRowOp, readonly string[]>>>> = {
  task: { create: ['title', 'description', 'deadline', 'priority'], update: ['title', 'description', 'deadline', 'priority'] },
  note: { create: ['title', 'description'], update: ['title', 'description'] },
  partner: { create: ['kind', ...PARTNER], update: PARTNER },
  contact: { create: CONTACT, update: CONTACT },
  stakeholder: { create: ['type', ...ANALYSIS, 'note'], update: ['type', ...ANALYSIS, 'note'] },
  participant: { create: ['projectRole', ...ANALYSIS], update: ['projectRole', ...ANALYSIS] },
  contract: { create: CONTRACT, update: CONTRACT },
  finance_entry: {
    create: ['title', 'plannedAmount', 'plannedDate', 'actualAmount', 'actualDate', 'note'],
    update: ['title', 'plannedAmount', 'plannedDate', 'note'],
  },
  metric_value: { update: ['current'] },
  review: { create: ['month', 'progress', 'issues', 'nextActions'], update: ['progress', 'issues', 'nextActions'] },
};

/** §8.2 "Loại bản ghi": a row kind's name, as the mapping bar names what a sheet's rows become. */
export const KIND_LABEL: Readonly<Record<BatchRowKind, string>> = {
  task: 'Việc', note: 'Ghi chú', partner: 'Đối tác', contact: 'Người liên hệ', stakeholder: 'Bên liên quan',
  participant: 'Người tham gia dự án', contract: 'Hợp đồng', finance_entry: 'Khoản tài chính',
  metric_value: 'Giá trị chỉ số', review: 'Rà soát tháng',
};

/**
 * What a sheet column can map to besides a field (the server's `schema.ts` STATEMENT_FIELDS and `hint.<key>` of
 * MATCH_HINT_KEYS): a statement's money columns, and a name code looks a record up by. A unit test holds the list
 * to the server's `parseMapping`.
 */
const STATEMENT_LABEL: Readonly<Record<string, string>> = {
  amountOut: 'Số tiền chi', amountIn: 'Số tiền thu', amount: 'Số tiền (âm là chi)',
};
const HINT_LABEL: Readonly<Record<string, string>> = {
  name: 'Tên để đối chiếu', title: 'Tiêu đề để đối chiếu', taxCode: 'Mã số thuế để đối chiếu',
  email: 'Email để đối chiếu', phone: 'Điện thoại để đối chiếu', contractNo: 'Số hợp đồng để đối chiếu',
  project: 'Tên dự án', partner: 'Tên đối tác', contact: 'Tên người liên hệ', assignee: 'Người nhận',
  category: 'Tên danh mục', objective: 'Tên mục tiêu', strategyPriority: 'Ưu tiên chiến lược', metric: 'Tên chỉ số',
};

/** The fields a column of a `kind` sheet can map to (§7.4): the kind's own, the statement's money, the match names. */
export function mappingFields(kind: BatchRowKind): readonly string[] {
  const own = FIELD_SUBSETS[kind].create ?? FIELD_SUBSETS[kind].update ?? [];
  return [...own, ...(kind === 'finance_entry' ? Object.keys(STATEMENT_LABEL) : []),
    ...Object.keys(HINT_LABEL).map((k) => `hint.${k}`)];
}

/** A mapping field's name in the bar: "Ngày thực tế", "Số tiền chi", "Tên dự án". */
export function mappingLabel(field: string): string {
  if (field.startsWith('hint.')) return HINT_LABEL[field.slice(5)] ?? field;
  return STATEMENT_LABEL[field] ?? INGEST_FIELD_LABELS[field] ?? field;
}

export type Editor =
  | { type: 'text' } | { type: 'long' } | { type: 'date' } | { type: 'month' } | { type: 'amount' }
  | { type: 'decimal' } | { type: 'labels' } | { type: 'choice'; choices: Readonly<Record<string, string>> };

const TEXT: Editor = { type: 'text' };
const LONG: Editor = { type: 'long' };
const DATE: Editor = { type: 'date' };
const AMOUNT: Editor = { type: 'amount' };
const choice = (choices: Readonly<Record<string, string>>): Editor => ({ type: 'choice', choices });

export const EDITOR_OF: Readonly<Record<string, Editor>> = {
  title: TEXT, description: LONG, deadline: DATE, priority: choice(PRIORITY_LABEL),
  kind: choice(PARTNER_KIND_LABEL), name: TEXT, labels: { type: 'labels' }, taxCode: TEXT, phone: TEXT, email: TEXT,
  address: TEXT, website: TEXT, note: LONG,
  type: choice(STAKEHOLDER_TYPE_LABEL), power: choice(LEVEL_LABEL), interest: choice(LEVEL_LABEL),
  engagementCurrent: choice(ENGAGEMENT_LABEL), engagementDesired: choice(ENGAGEMENT_LABEL), strategyNote: LONG,
  projectRole: TEXT,
  contractNo: TEXT, value: AMOUNT, startDate: DATE, endDate: DATE, status: choice(CONTRACT_STATUS_LABEL),
  plannedAmount: AMOUNT, plannedDate: DATE, actualAmount: AMOUNT, actualDate: DATE,
  current: { type: 'decimal' }, month: { type: 'month' }, progress: LONG, issues: LONG, nextActions: LONG,
};

const EMPTY = '—';

/** A field's value as the app writes it elsewhere: dd/MM/yyyy, "30.000.000 ₫", the Vietnamese name of a code. */
export function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  const editor = EDITOR_OF[field];
  if (Array.isArray(value)) {
    const labels = value.map((v) => (field === 'labels' ? PARTNER_LABEL_LABEL[v as keyof typeof PARTNER_LABEL_LABEL] ?? String(v)
      : String(v)));
    return labels.length === 0 ? EMPTY : labels.join(', ');
  }
  const text = typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' ? String(value) : JSON.stringify(value);
  switch (editor?.type) {
    case 'date': return formatDate(text);
    case 'month': return /^\d{4}-\d{2}$/.test(text) ? monthLabel(text) : text;
    case 'amount': return /^-?\d+$/.test(text) ? `${formatMoney(text)} ₫` : text;
    case 'choice': return editor.choices[text] ?? text;
    default: return text;
  }
}

/** The day without its year when it is this year's, as the mockup's meta line writes a deadline ("hạn 07/10"). */
export function shortDay(iso: unknown, now = new Date()): string | null {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const full = formatDate(iso);
  return iso.startsWith(String(now.getFullYear())) ? full.slice(0, 5) : full;
}

/** The references each row kind uses (the server's `options.ts` USES); a pick of anything else is refused. */
export const USES: Readonly<Record<BatchRowKind, readonly Exclude<BatchOptionFor, 'target'>[]>> = {
  task: [], note: [], partner: [],
  contact: ['partner'],
  stakeholder: ['project', 'partner'],
  participant: ['project', 'partner', 'contact', 'stakeholder'],
  contract: ['project', 'partner', 'stakeholder'],
  finance_entry: ['project', 'category'],
  metric_value: ['objective'],
  review: ['objective'],
};

export const REF_KEY: Readonly<Record<Exclude<BatchOptionFor, 'target'>, BatchRefKey>> = {
  project: 'projectId', partner: 'partnerId', contact: 'contactId', stakeholder: 'stakeholderId',
  category: 'categoryId', objective: 'objectiveId',
};

/** The matcher's "pick one" sentences (`match/decide.ts` CHOOSE_REF and CHOOSE), held to them by a unit test. */
const CHOOSE_REF: Readonly<Record<Exclude<BatchOptionFor, 'target'>, string>> = {
  project: 'Chọn dự án', partner: 'Chọn đối tác', contact: 'Chọn người liên hệ', stakeholder: 'Chọn bên liên quan',
  category: 'Chọn danh mục', objective: 'Chọn mục tiêu',
};
const CHOOSE_TARGET: Readonly<Record<BatchRowKind, string>> = {
  partner: 'Chọn đối tác', contact: 'Chọn người liên hệ', stakeholder: 'Chọn bên liên quan',
  participant: 'Chọn người tham gia', contract: 'Chọn hợp đồng', task: 'Chọn việc', note: 'Chọn ghi chú',
  finance_entry: 'Chọn khoản tài chính', metric_value: 'Chọn chỉ số', review: 'Chọn bản rà soát',
};

/**
 * The list a needs_fix reason asks the person to pick from, or null when it asks for something else. One sentence
 * can name either ("Chọn đối tác"): it is the reference when the kind uses that reference, else the row's target.
 */
export function pickOf(kind: BatchRowKind, reason: string): BatchOptionFor | null {
  for (const of of USES[kind]) if (CHOOSE_REF[of] === reason) return of;
  return CHOOSE_TARGET[kind] === reason ? 'target' : null;
}

/** "Chọn …" for a list: the reason's own words, so the button reads what the row asks. */
export const chooseLabel = (kind: BatchRowKind, of: BatchOptionFor): string =>
  of === 'target' ? CHOOSE_TARGET[kind] : CHOOSE_REF[of];

/** Fields code fills from the parent card (`check/inherit.ts`), as the meta line names them. */
export const FILLED_NAME: Readonly<Record<string, string>> = { deadline: 'hạn', assigneeId: 'người nhận' };
